/**
 * Showtime V2 - Contact Modal
 * Single reusable modal for Make an Offer / See It In Store / General Contact.
 * Progressive enhancement: triggers are links; JS intercepts to open modal.
 */

(function() {
  'use strict';

  const modalOverlay = document.querySelector('[data-contact-modal]');
  if (!modalOverlay) return;

  const dialog = modalOverlay.querySelector('[data-contact-dialog]');
  const form = modalOverlay.querySelector('[data-contact-form]');
  const closeBtn = modalOverlay.querySelector('[data-contact-close]');
  const titleEl = modalOverlay.querySelector('[data-contact-title]');
  const subtitleEl = modalOverlay.querySelector('[data-contact-subtitle]');
  const errorsEl = modalOverlay.querySelector('[data-contact-errors]');
  const successEl = modalOverlay.querySelector('[data-contact-success]');
  const submitBtn = modalOverlay.querySelector('[data-contact-submit]');
  const bodyInput = modalOverlay.querySelector('[data-contact-body]');
  const fieldModeEls = Array.from(modalOverlay.querySelectorAll('[data-contact-mode-field]'));
  const requiredEls = Array.from(modalOverlay.querySelectorAll('[data-contact-required]'));

  if (!dialog || !form) return;

  const decodeHtml = (value) => {
    if (!value) return '';
    if (!value.includes('&')) return value;
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  };

  const copy = {
    title: {
      offer: decodeHtml(modalOverlay.dataset.contactTitleOffer) || 'Make an Offer',
      store: decodeHtml(modalOverlay.dataset.contactTitleStore) || 'See It In Store',
      general: decodeHtml(modalOverlay.dataset.contactTitleGeneral) || 'Contact Us',
      sizing: decodeHtml(modalOverlay.dataset.contactTitleSizing) || 'Ring Sizing Inquiry'
    },
    subtitle: {
      offer: decodeHtml(modalOverlay.dataset.contactSubtitleOffer) || '',
      store: decodeHtml(modalOverlay.dataset.contactSubtitleStore) || '',
      general: decodeHtml(modalOverlay.dataset.contactSubtitleGeneral) || '',
      sizing: decodeHtml(modalOverlay.dataset.contactSubtitleSizing) || ''
    },
    submit: {
      offer: decodeHtml(modalOverlay.dataset.contactSubmitOffer) || 'Send offer',
      store: decodeHtml(modalOverlay.dataset.contactSubmitStore) || 'Request visit',
      general: decodeHtml(modalOverlay.dataset.contactSubmitGeneral) || 'Send message',
      sizing: decodeHtml(modalOverlay.dataset.contactSubmitSizing) || 'Send sizing request'
    },
    request: {
      offer: decodeHtml(modalOverlay.dataset.contactRequestOffer) || 'Make an Offer',
      store: decodeHtml(modalOverlay.dataset.contactRequestStore) || 'See It In Store',
      general: decodeHtml(modalOverlay.dataset.contactRequestGeneral) || 'General Contact',
      sizing: decodeHtml(modalOverlay.dataset.contactRequestSizing) || 'Ring Sizing Inquiry'
    },
    errorHeading: decodeHtml(modalOverlay.dataset.contactErrorHeading) || 'Please complete the required fields:',
    errorEmail: decodeHtml(modalOverlay.dataset.contactErrorEmail) || 'Enter a valid email address.'
  };

  const subjectPrefix = modalOverlay.dataset.contactSubjectPrefix || '';

  let activeTrigger = null;
  let currentMode = 'general';
  let removeKeyListener = null;
  let cachedProduct = undefined;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  const getFocusable = () => Array.from(dialog.querySelectorAll(focusableSelector))
    .filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);

  const isVisible = (element) => {
    if (!element) return false;
    if (element.hasAttribute('hidden')) return false;
    if (element.closest('[hidden]')) return false;
    return element.offsetParent !== null;
  };

  const detectModeFromFields = () => {
    const offerField = form?.querySelector('[data-contact-offer]');
    const dayField = form?.querySelector('[data-contact-day]');
    const sizingField = form?.querySelector('[data-contact-desired-size]');
    if (offerField && isVisible(offerField)) return 'offer';
    if (dayField && isVisible(dayField)) return 'store';
    if (sizingField && isVisible(sizingField)) return 'sizing';
    return null;
  };

  const getActiveMode = () => detectModeFromFields()
    || form?.dataset.contactMode
    || modalOverlay.dataset.contactMode
    || currentMode
    || 'general';

  const setMode = (mode) => {
    currentMode = mode;
    modalOverlay.dataset.contactMode = mode;
    if (form) form.dataset.contactMode = mode;
    if (titleEl) titleEl.textContent = copy.title[mode] || copy.title.general;
    if (subtitleEl) {
      const subtitleText = copy.subtitle[mode] || '';
      subtitleEl.textContent = subtitleText;
      subtitleEl.hidden = subtitleText === '';
    }
    if (submitBtn) submitBtn.textContent = copy.submit[mode] || copy.submit.general;

    fieldModeEls.forEach((el) => {
      const modes = (el.dataset.contactModeField || '').split(',').map((item) => item.trim());
      el.hidden = !modes.includes(mode);
    });

    requiredEls.forEach((input) => {
      const modes = (input.dataset.contactRequired || '').split(',').map((item) => item.trim());
      input.required = modes.includes(mode);
      if (!input.required) {
        input.classList.remove('is-invalid');
      }
    });
  };

  const clearState = () => {
    if (errorsEl) {
      errorsEl.innerHTML = '';
      errorsEl.hidden = true;
    }
    if (successEl) {
      successEl.hidden = true;
    }
    form.classList.remove('is-success');
    Array.from(form.querySelectorAll('.is-invalid')).forEach((el) => {
      el.classList.remove('is-invalid');
    });
  };

  const openModal = (mode, trigger) => {
    activeTrigger = trigger || document.activeElement;
    setMode(mode || 'general');
    clearState();
    modalOverlay.hidden = false;
    modalOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('contact-modal-open');

    // ADA SW-6: Hide background content from assistive tech
    const mainContent = document.getElementById('main-content') || document.querySelector('main');
    const headerEl = document.querySelector('header');
    const footerEl = document.querySelector('footer');
    if (mainContent) mainContent.inert = true;
    if (headerEl) headerEl.inert = true;
    if (footerEl) footerEl.inert = true;

    const initialFocus = modalOverlay.querySelector('[data-contact-initial-focus]');
    if (initialFocus && !initialFocus.hasAttribute('hidden')) {
      initialFocus.focus();
    } else {
      const focusable = getFocusable();
      if (focusable.length) {
        focusable[0].focus();
      } else {
        dialog.focus();
      }
    }

    if (!removeKeyListener) {
      const handleKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeModal();
          return;
        }
        if (event.key !== 'Tab') return;
        const focusables = getFocusable();
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', handleKeydown);
      removeKeyListener = () => document.removeEventListener('keydown', handleKeydown);
    }
  };

  const closeModal = () => {
    modalOverlay.hidden = true;
    modalOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('contact-modal-open');

    // ADA SW-6: Restore background content to assistive tech
    const mainContent = document.getElementById('main-content') || document.querySelector('main');
    const headerEl = document.querySelector('header');
    const footerEl = document.querySelector('footer');
    if (mainContent) mainContent.inert = false;
    if (headerEl) headerEl.inert = false;
    if (footerEl) footerEl.inert = false;
    if (removeKeyListener) {
      removeKeyListener();
      removeKeyListener = null;
    }
    if (activeTrigger && typeof activeTrigger.focus === 'function') {
      activeTrigger.focus();
    }
  };

  const getProductData = () => {
    if (cachedProduct !== undefined) return cachedProduct;
    const section = document.querySelector('.section-main-product');
    if (!section) {
      cachedProduct = null;
      return cachedProduct;
    }
    const script = section.querySelector('[data-product-json]');
    if (!script) {
      cachedProduct = null;
      return cachedProduct;
    }
    try {
      cachedProduct = JSON.parse(script.textContent);
    } catch (error) {
      cachedProduct = null;
    }
    return cachedProduct;
  };

  const getCurrentVariant = (product) => {
    if (!product || !product.variants) return null;
    const idInput = document.querySelector('[data-product-form] [name="id"]');
    const variantId = idInput ? String(idInput.value) : null;
    if (!variantId) return null;
    return product.variants.find((variant) => String(variant.id) === variantId) || null;
  };

  const getProductContext = () => {
    // Prefer data attributes on the trigger element
    const t = activeTrigger;
    if (t && t.dataset && t.dataset.productTitle) {
      return {
        title: t.dataset.productTitle || '',
        handle: t.dataset.productHandle || '',
        id: t.dataset.productId || '',
        sku: t.dataset.sku || '',
        variantTitle: t.dataset.variantTitle || '',
        variantId: t.dataset.variantId || '',
        url: t.dataset.url || ''
      };
    }

    // Fallback: window-level product data (ShowtimeTheme or JSON blob)
    const product = getProductData();
    const variant = getCurrentVariant(product);
    if (product) {
      return {
        title: product.title || '',
        handle: product.handle || '',
        id: product.id || '',
        sku: variant?.sku || '',
        variantTitle: variant && variant.title && variant.title !== 'Default Title' ? variant.title : '',
        variantId: variant?.id || '',
        url: window.location.href
      };
    }

    return null;
  };

  const buildSubject = (mode, context) => {
    const requestLabel = copy.request[mode] || copy.request.general;
    const parts = [];
    if (subjectPrefix) parts.push(subjectPrefix);
    if (requestLabel) parts.push(requestLabel);
    if (context && context.sku) parts.push(context.sku);
    return parts.join(' - ').trim();
  };

  const formatOffer = (value) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    return trimmed.startsWith('$') ? trimmed : `$${trimmed}`;
  };

  const buildBody = (mode, context) => {
    const requestLabel = copy.request[mode] || copy.request.general;
    const name = form.querySelector('[name="contact[name]"]')?.value?.trim();
    const email = form.querySelector('[name="contact[email]"]')?.value?.trim();
    const phone = form.querySelector('[name="contact[phone]"]')?.value?.trim();
    const offer = form.querySelector('[data-contact-offer]')?.value?.trim();
    const day = form.querySelector('[data-contact-day]')?.value?.trim();
    const time = form.querySelector('[data-contact-time]')?.value?.trim();
    const desiredSize = form.querySelector('[data-contact-desired-size]')?.value?.trim();
    const currentSize = activeTrigger?.dataset?.currentRingSize?.trim() || '';
    const message = form.querySelector('[data-contact-message]')?.value?.trim();

    const lines = [];
    lines.push(`Subject: ${buildSubject(mode, context)}`);
    lines.push('');
    lines.push(`Request Type: ${requestLabel}`);

    if (context) {
      if (context.title) lines.push(`Product: ${context.title}`);
      if (context.handle) lines.push(`Handle: ${context.handle}`);
      if (context.id) lines.push(`Product ID: ${context.id}`);
      if (context.sku) {
        lines.push(`SKU: ${context.sku}`);
      }
      if (context.variantTitle) lines.push(`Variant: ${context.variantTitle}`);
      if (context.variantId && !context.sku) lines.push(`Variant ID: ${context.variantId}`);
    }

    if (mode === 'offer') {
      const formattedOffer = formatOffer(offer);
      if (formattedOffer) lines.push(`Offer: ${formattedOffer}`);
    }

    if (mode === 'store') {
      if (day) lines.push(`Preferred day: ${day}`);
      if (time) lines.push(`Preferred time: ${time}`);
    }

    if (mode === 'sizing') {
      if (currentSize) lines.push(`Current listed size: ${currentSize}`);
      if (desiredSize) lines.push(`Desired size: ${desiredSize}`);
    }

    if (name || email || phone) {
      lines.push(`Customer: ${[name, email, phone].filter(Boolean).join(', ')}`);
    }

    if (message) lines.push(`Message: ${message}`);
    lines.push(`Page URL: ${(context && context.url) || window.location.href}`);

    return lines.join('\n');
  };

  const getRequiredInputs = () => {
    const activeMode = getActiveMode();

    const modeInputs = Array.from(form.querySelectorAll('[data-contact-required]')).filter((input) => {
      const modes = (input.dataset.contactRequired || '').split(',').map((item) => item.trim());
      return modes.includes(activeMode);
    });

    return modeInputs.filter((input) => isVisible(input));
  };

  const validate = () => {
    const errors = [];
    const invalidFields = [];

    const requiredInputs = getRequiredInputs();
    requiredInputs.forEach((input) => {
      const value = input.value.trim();
      if (!value) {
        invalidFields.push(input);
        const label = input.dataset.contactLabel || input.name || 'Field';
        errors.push(label);
      }
    });

    const emailInput = form.querySelector('[name="contact[email]"]');
    if (emailInput && emailInput.value.trim()) {
      const emailValue = emailInput.value.trim();
      if (emailInput.validity.typeMismatch || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        invalidFields.push(emailInput);
        errors.push(copy.errorEmail);
      }
    }

    if (errors.length) {
      const uniqueErrors = Array.from(new Set(errors));
      if (errorsEl) {
        errorsEl.innerHTML = `
          <p>${copy.errorHeading}</p>
          <ul>${uniqueErrors.map((error) => `<li>${error}</li>`).join('')}</ul>
        `;
        errorsEl.hidden = false;
        errorsEl.focus && errorsEl.focus();
      }
      invalidFields.forEach((input) => input.classList.add('is-invalid'));
      return false;
    }

    if (errorsEl) {
      errorsEl.innerHTML = '';
      errorsEl.hidden = true;
    }
    invalidFields.forEach((input) => input.classList.remove('is-invalid'));
    return true;
  };

  const showSuccess = () => {
    if (successEl) {
      successEl.hidden = false;
    }
    form.classList.add('is-success');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
    clearState();

    if (!validate()) {
      if (submitBtn) submitBtn.disabled = false;
      return;
    }

    const context = getProductContext();
    const activeMode = getActiveMode();

    /* Hook: allow page-level overrides for custom body building (e.g., multi-product wishlist) */
    const bodyEvent = new CustomEvent('contact:buildBody', {
      detail: {
        mode: activeMode,
        context: context,
        trigger: activeTrigger,
        form: form,
        subjectPrefix: subjectPrefix,
        body: null
      }
    });
    document.dispatchEvent(bodyEvent);

    const bodyContent = bodyEvent.detail.body || buildBody(activeMode, context);
    if (bodyInput) {
      bodyInput.value = bodyContent;
    }

    if (submitBtn) submitBtn.disabled = true;

    // Submit natively — Shopify may require a CAPTCHA redirect that fetch cannot handle.
    // The browser will follow the redirect chain and land on ?contact_posted=true on success.
    form.submit();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-contact-trigger]');
    if (!trigger) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (trigger.getAttribute('aria-disabled') === 'true' || trigger.hasAttribute('disabled')) return;
    event.preventDefault();
    const mode = trigger.dataset.contactMode || 'general';
    openModal(mode, trigger);
  });

  modalOverlay.addEventListener('click', (event) => {
    if (event.target === modalOverlay) {
      closeModal();
    }
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', (event) => {
      event.preventDefault();
      closeModal();
    });
  }

  form.addEventListener('submit', handleSubmit);

  // On page load: if Shopify redirected back with ?contact_posted=true, show success
  if (window.location.search.indexOf('contact_posted=true') !== -1) {
    // Open modal in a neutral state — we don't know which mode was used
    openModal('general', null);
    // Hide the mode-specific title and subtitle so only the success message shows
    if (titleEl) titleEl.hidden = true;
    if (subtitleEl) subtitleEl.hidden = true;
    showSuccess();
    // Clean up the URL so a refresh doesn't re-show the message
    if (window.history.replaceState) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('contact_posted');
      cleanUrl.hash = '';
      window.history.replaceState({}, '', cleanUrl.toString());
    }
  }
})();
